<template>
  <div class="return-date-picker" v-click-outside="closeCalendar">
    <div class="selected-date">
      <button class="return-date-picker__toggle-button" @click="toggleCalendar">
        <span class="return-date-picker__display-date" v-if="formattedDate">
          <svg class="return-date-picker__calendar-icon">
            <use :xlink:href="`#${calendar}`" />
          </svg>
          <div class="return-date-picker__display-text">
            <span class="selected-date__label">
              {{ $t('search.returnDate') }}
            </span>
            {{ formattedDate }}
          </div>
        </span>
        <span class="return-date-picker__add-trip" v-else>
          + {{ $t('search.returnDate') }}
        </span>
      </button>
      <button
        v-if="formattedDate"
        class="return-date-picker__cancel"
        @click="cancelDate"
      >
        <svg class="return-date-picker__cancel-icon">
          <use :xlink:href="`#${cancel}`" />
        </svg>
      </button>
    </div>

    <div v-if="showCalendar" class="return-date-picker__calendar-overlay">
      <v-date-picker
        ref="datepicker"
        v-model="selectedDate"
        @update:model-value="handleDateSelect"
        :columns="1"
        :locale="getCurrentLocale()"
        class="vc-custom"
        :min-date="minDate"
        :timezone="'UTC'"
        :initial-page="initialPage"
        :masks="{
          title: 'MMMM YYYY',
          weekdays: 'WWW',
          input: ['L', 'DD.MM.YYYY', 'DD.MM.YY'],
          dayPopover: 'WWW, MMM D, YYYY',
        }"
      >
        <template #nav-left-button="{ move }">
          <button type="button" @click="() => move()" class="vc-arrow-left">
            ‹
          </button>
        </template>
        <template #nav-right-button="{ move }">
          <button type="button" @click="() => move()" class="vc-arrow-right">
            ›
          </button>
        </template>
      </v-date-picker>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue';
  import { DatePicker } from 'v-calendar';
  import { useTravelSearchStore } from '@/stores/travelSearchStore';
  import { format } from 'date-fns';
  import { enGB, nb } from 'date-fns/locale';
  import calendar from '@/assets/icons/calendar.svg';
  import cancel from '@/assets/icons/cancel.svg';
  import { getCurrentLocale } from '@/i18n';
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService';

  export default defineComponent({
    name: 'ReturnDate',
    components: {
      VDatePicker: DatePicker,
    },
    directives: {
      clickOutside: {
        beforeMount(el, binding) {
          el.clickOutsideEvent = (event: MouseEvent) => {
            if (!(el === event.target || el.contains(event.target))) {
              binding.value(event, el);
            }
          };
          document.body.addEventListener('click', el.clickOutsideEvent);
        },
        unmounted(el) {
          document.body.removeEventListener('click', el.clickOutsideEvent);
        },
      },
    },
    setup() {
      const travelSearchStore = useTravelSearchStore();
      const showCalendar = ref(false);
      const selectedDate = ref<Date | null>(null);
      const datepicker = ref<any>(null);

      const minDate = computed(() => {
        const today = new Date(); // Use today instead of hardcoded April
        const departureDate = travelSearchStore.departureDate
          ? new Date(travelSearchStore.departureDate)
          : today;
        return departureDate > today ? departureDate : today; // Ensure minDate is at least today
      });

      const initialPage = computed(() => {
        let dateToUse: Date;

        if (travelSearchStore.returnDate) {
          dateToUse = new Date(travelSearchStore.returnDate);
        } else if (travelSearchStore.departureDate) {
          dateToUse = new Date(travelSearchStore.departureDate);
        } else {
          dateToUse = new Date();
        }

        return {
          month: dateToUse.getMonth() + 1,
          year: dateToUse.getFullYear(),
        };
      });

      const currentDateLocale = computed(() => {
        return getCurrentLocale() === 'no' ? nb : enGB;
      });

      const formattedDate = computed(() => {
        if (!selectedDate.value) return '';
        return format(selectedDate.value, 'dd. MMM', {
          locale: currentDateLocale.value,
        }).toLowerCase();
      });

      const toggleCalendar = () => {
        showCalendar.value = !showCalendar.value;
      };

      const closeCalendar = () => {
        showCalendar.value = false;
      };

      const handleDateSelect = (date: Date) => {
        selectedDate.value = date;
        showCalendar.value = false;
        travelSearchStore.setReturnDate(format(date, 'yyyy-MM-dd'));
        pushInteractionToDatalayer('add return date', { selection: format(date, 'yyyy-MM-dd') });
      };

      watch(
        () => travelSearchStore.returnDate,
        (newReturnDate) => {
          selectedDate.value = newReturnDate ? new Date(newReturnDate) : null;
        },
        { immediate: true },
      );

      const cancelDate = () => {
        selectedDate.value = null;
        travelSearchStore.setReturnDate(null);
      };

      return {
        showCalendar,
        selectedDate,
        datepicker,
        calendar,
        cancel,
        formattedDate,
        toggleCalendar,
        closeCalendar,
        handleDateSelect,
        cancelDate,
        minDate,
        initialPage,
        getCurrentLocale,
      };
    },
  });
</script>

<style lang="scss" scoped>
  .return-date-picker {
    display: flex;
    flex-direction: column;
    position: relative;

    .selected-date {
      @include body-large;
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 160px;
      padding: 0;
      text-align: left;
      color: $clr-text;
      background-color: $clr-background;
      border: 1px solid $clr-primary-50;
      border-radius: $border-radius;
      box-shadow: $box-shadow-card;

      @media screen and (min-width: $breakpoint-sm) {
        min-width: 165px;
      }

      &__label {
        position: absolute;
        top: rem(10);
        left: 40px;
        color: $clr-heading;
      }
    }
    &__toggle-button {
      @include body-large;
      display: flex;
      flex-direction: row;
      justify-content: center;
      width: 100%;
      padding: 1px;
      background: transparent;
      border: none;
      border-radius: $border-radius;

      svg {
        position: relative;
        top: -10px;
        left: 0;
        width: 15px;
        height: 15px;
        color: $clr-secondary;
        margin-right: 8px;
      }
    }
    &__display-date {
      padding: rem(28) 0 rem(10) 15px;
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
    }
    &__display-text {
      display: flex;
      flex-direction: column;
      text-align: left;
      span {
        @include body-small;
      }
    }

    &__add-trip {
      @include body-large;
      padding: rem(19) 0;
      color: $clr-text;
    }

    &__calendar-icon {
      width: 15px;
      height: 15px;
      color: $clr-secondary;
      margin-right: 8px;
    }

    &__cancel {
      background: transparent;
      border: none;
      padding: rem(16) 16px;
      margin-left: auto;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    &__cancel-icon {
      width: 16px;
      height: 16px;
      color: $clr-text;
    }

    &__calendar-overlay {
      position: absolute;
      top: 100%;
      z-index: 1000;
      left: 50%;
      transform: translateX(-50%);
      background-color: white;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      @media (max-width: $breakpoint-xs) {
        left: initial;
      }
    }
  }

  :deep(.vc-custom) {
    .vc-header {
      color: #2a347a;

      .vc-title {
        color: #2a347a;
        background: transparent !important;

        &:hover {
          background: transparent !important;
        }
      }

      .vc-arrow {
        background: transparent !important;
      }

      button {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Calendar title button (month/year)
    .vc-title-wrapper {
      button {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Navigation buttons
    .vc-arrow-left,
    .vc-arrow-right,
    button.vc-nav-arrow {
      width: 26px !important;
      height: 26px !important;
      background: transparent !important;
      border: none !important;
      padding: 4px !important;
      font-size: 20px !important;
      color: #2a347a !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      &:hover {
        background: transparent !important;
      }
    }

    // Month/year popover styles
    .vc-popover-content {
      * {
        background: white !important;
        &:hover {
          background: #f4f5f8 !important;
        }
      }

      .vc-nav-title {
        background: transparent !important;
      }

      button {
        background: transparent !important;
        &:hover {
          background: #f4f5f8 !important;
        }
      }
    }

    // Force transparent background on all navigation elements
    [class*='vc-'] {
      &[class*='-arrow'],
      &[class*='-button'],
      &[class*='-nav'],
      &[class*='-title'] {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Highlight styles
    .vc-highlight {
      background-color: #2a347a !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;

      &.vc-day-content {
        color: white !important;
        background-color: #2a347a !important;
        border-radius: 0 !important;
      }
    }

    // Target all possible disabled date scenarios
    .vc-day.is-disabled,
    .vc-day.disabled,
    .vc-day[aria-disabled='true'] {
      .vc-day-content {
        background-color: #f4f5f8 !important;
        border-radius: 0 !important;
        opacity: 1 !important;
        color: #666 !important;
        cursor: not-allowed !important;
        width: 32px !important;
        height: 32px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        position: relative !important;
        margin: 0 auto !important;

        &:hover {
          background-color: #f4f5f8 !important;
          cursor: not-allowed !important;
        }
      }
    }

    // Additional specificity for disabled dates
    .vc-container .vc-wrapper .vc-weeks .vc-day.is-disabled .vc-day-content {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
    }

    // Force disabled styles
    .vc-day-content[aria-disabled='true'],
    .vc-day-content.is-disabled {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
      cursor: not-allowed !important;
    }
  }

  // Global styles to ensure disabled dates are styled
  :deep(.vc-day.is-disabled),
  :deep(.vc-day.disabled),
  :deep(.vc-day[aria-disabled='true']) {
    .vc-day-content {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
      cursor: not-allowed !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      color: #666 !important;
    }
  }
</style>
